// src/shared/lib/markdown/safeMarkdown.js
//
// Безопасный рендер markdown / HTML для использования с v-html.
//
// Причина существования: marked@>=0.7 НЕ санитизирует HTML на выходе.
// `marked.parse("<img src=x onerror=alert(1)>")` вернёт ровно то, что
// прислали → если это попадёт в v-html, скрипт выполнится. Для AI-чата,
// где контент сообщения контролируется внешним источником (модель, история,
// prompt-injection), это рабочий XSS-вектор.
//
// Решение: `marked.parse` → `DOMPurify.sanitize` со строгим allowlist'ом
// тегов и атрибутов, и блокировка `javascript:` / `data:` URL.
// См. audit #1 + docs/auth-token-security.md.

import { marked } from "marked";
import DOMPurify from "dompurify";

marked.setOptions({ breaks: true, gfm: true });

// Allowlist — только то, что нужно для чат-сообщений с базовым форматированием
// + таблицы (GFM). НЕ разрешаем: script, style, iframe, object, embed, form,
// input, button, link, meta — всё что может загрузить ресурс или выполнить код.
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "hr",
    "strong",
    "em",
    "u",
    "s",
    "code",
    "pre",
    "blockquote",
    "ul",
    "ol",
    "li",
    "a",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "div",
    "span",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
  ],
  ALLOWED_ATTR: ["href", "class", "target", "rel"],
  // Блокирует javascript:, data:, vbscript: и т.п. URL.
  ALLOWED_URI_REGEXP: /^(?:https?|mailto):/i,
};

// Все <a> с http(s)-href получают `target="_blank"` + `rel="noopener noreferrer"`.
// Иначе открытие ссылки из чата может tabnabbing'ом перехватить window.opener.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("href")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

/**
 * Парсит markdown в HTML и санитизирует. Готово для v-html.
 * @param {string} msg — markdown-источник (например, ответ AI)
 * @returns {string} безопасный HTML
 */
export function safeMarkdown(msg) {
  if (!msg) return "";
  const html = marked.parse(String(msg));
  return DOMPurify.sanitize(html, PURIFY_CONFIG);
}

/**
 * Санитизирует уже готовый HTML — для случаев когда markdown не нужен
 * (например, message в ConfirmationModal, где иногда хочется <br> / <strong>,
 * но source может содержать пользовательские данные).
 * @param {string} html
 * @returns {string} безопасный HTML
 */
export function safeHtml(html) {
  if (!html) return "";
  return DOMPurify.sanitize(String(html), PURIFY_CONFIG);
}
